import { Injectable } from "@angular/core";
import { updateMatchReviews } from "../api.service";
import {
  MatchReview,
  MatchReviews
} from "../../../../shared/models/matchReviews";
import { Manufacturer } from "../../../../shared/models/manufacturer";
import { Z2DataMatch } from "../../../../shared/models/dataProviders/z2data";

export interface SuitableWorkflowContext {
  customer: string;
  composedID: string;
  manufacturerPartNumberClean: string;
  matchReviews: MatchReviews;
  currentManufacturer: Manufacturer;
  manufacturerMatches: Z2DataMatch[];
}

export type SuitableSetAllAction =
  | "none"
  | "confirmMarkAllAsNotSuitable"
  | "confirmUnmarkAllSuitable"
  | "applyDirectly";

export type SetSuitableValueResult =
  | {
      action: "openConfirmSuitableChange";
      pendingUnsuitableMatch: Z2DataMatch;
      checked: boolean;
      revertedChecked: boolean;
      messageKey: SuitableConfirmMessageKey;
    }
  | {
      action: "applied";
      updatedManufacturer: Manufacturer | null;
    };

export type SetAllSuitableValueResult =
  | { action: "none" }
  | { action: "openConfirmAllUnsuitable" }
  | { action: "openConfirmUnmarkAllSuitable" }
  | { action: "applied"; updatedManufacturer: Manufacturer | null };

export type SuitableConfirmMessageKey =
  | "markAsNotSuitableConfirm"
  | "unmarkOptionAsNotSuitableConfirm";

type UpdateManufacturerFn = (
  updatedManufacturer: Manufacturer,
  currentManufacturer?: Manufacturer
) => Promise<Manufacturer>;

@Injectable({
  providedIn: "root"
})
export class ExternalDataFilterSuitableService {
  getSuitableNote(matchReviews: MatchReviews | undefined, match: Z2DataMatch) {
    const matchReview = this.getMatchReview(matchReviews, match);
    return matchReview?.note || "";
  }

  getMatchPartId(match: Z2DataMatch) {
    const matchWithFallbacks = match as any;
    return Number(
      matchWithFallbacks.PartID ??
        matchWithFallbacks.partId ??
        matchWithFallbacks.partID ??
        matchWithFallbacks.id ??
        matchWithFallbacks.z2DataId
    );
  }

  isSuitableChecked(matchReviews: MatchReviews | undefined, match: Z2DataMatch) {
    const matchReview = this.getMatchReview(matchReviews, match);
    return matchReview?.notSuitable === true;
  }

  isAllSuitableChecked(
    matchReviews: MatchReviews | undefined,
    manufacturerMatches?: Z2DataMatch[]
  ) {
    const manufacturerPartIds = Array.isArray(manufacturerMatches)
      ? Array.from(
          new Set(
            manufacturerMatches
              .map((match: Z2DataMatch) => this.getMatchPartId(match))
              .filter((partId: number) => !Number.isNaN(partId))
          )
        )
      : [];

    const notSuitablePartIds = new Set(
      (matchReviews?.matches || [])
        .filter((review: MatchReview) => review.notSuitable === true)
        .map((review: MatchReview) => Number(review.partId))
        .filter((partId: number) => !Number.isNaN(partId))
    );

    if (manufacturerPartIds.length === 0) {
      return false;
    }

    if (manufacturerPartIds.length !== notSuitablePartIds.size) {
      return false;
    }

    return manufacturerPartIds.every((partId: number) =>
      notSuitablePartIds.has(partId)
    );
  }

  hasAnyMatches(matchReviews: MatchReviews | undefined) {
    const matches = matchReviews?.matches;
    return !!matches && matches.length > 0;
  }

  hasAnyNotSuitableReview(
    matchReviews: MatchReviews | undefined
  ) {
    if (!matchReviews || !Array.isArray(matchReviews.matches)) {
      return false;
    }

    return matchReviews.matches.some(
      (matchReview: MatchReview) => matchReview.notSuitable === true
    );
  }

  hasAnyManufacturerMatches(manufacturerMatches?: Z2DataMatch[]) {
    return Array.isArray(manufacturerMatches) && manufacturerMatches.length > 0;
  }

  shouldConfirmMarkAsNotSuitable(
    matchReviews: MatchReviews | undefined,
    match: Z2DataMatch,
    checked: boolean
  ) {
    return checked && !this.isSuitableChecked(matchReviews, match);
  }

  shouldConfirmUnmarkAsNotSuitable(
    matchReviews: MatchReviews | undefined,
    match: Z2DataMatch,
    checked: boolean
  ) {
    return !checked && this.isSuitableChecked(matchReviews, match);
  }

  getSuitableConfirmMessageKey(checked: boolean): SuitableConfirmMessageKey {
    return checked
      ? "markAsNotSuitableConfirm"
      : "unmarkOptionAsNotSuitableConfirm";
  }

  getInitialSuitableChangeNote(
    matchReviews: MatchReviews | undefined,
    match: Z2DataMatch,
    checked: boolean
  ) {
    if (checked) {
      return "";
    }

    return this.getSuitableNote(matchReviews, match);
  }

  getSetAllSuitableAction(
    matchReviews: MatchReviews | undefined,
    checked: boolean,
    manufacturerMatches?: Z2DataMatch[]
  ): SuitableSetAllAction {
    if (
      !this.hasAnyMatches(matchReviews) &&
      !this.hasAnyManufacturerMatches(manufacturerMatches)
    ) {
      return "none";
    }

    const allSuitableChecked = this.isAllSuitableChecked(
      matchReviews,
      manufacturerMatches
    );
    if (checked && !allSuitableChecked) {
      return "confirmMarkAllAsNotSuitable";
    }

    if (!checked && allSuitableChecked) {
      return "confirmUnmarkAllSuitable";
    }

    return "applyDirectly";
  }

  async setSuitableValue(
    context: SuitableWorkflowContext,
    match: Z2DataMatch,
    checked: boolean
  ): Promise<SetSuitableValueResult> {
    if (
      this.shouldConfirmMarkAsNotSuitable(context.matchReviews, match, checked) ||
      this.shouldConfirmUnmarkAsNotSuitable(context.matchReviews, match, checked)
    ) {
      return {
        action: "openConfirmSuitableChange",
        pendingUnsuitableMatch: match,
        checked,
        revertedChecked: !checked,
        messageKey: this.getSuitableConfirmMessageKey(checked)
      };
    }

    const updatedManufacturer = await this.applySingleSuitableChange(
      context,
      match,
      checked
    );

    return { action: "applied", updatedManufacturer };
  }

  async confirmMarkAsNotSuitable(
    context: SuitableWorkflowContext,
    pendingUnsuitableMatch: Z2DataMatch | null,
    note: string = "",
    checked: boolean = true
  ): Promise<Manufacturer | null> {
    if (pendingUnsuitableMatch == null) {
      return null;
    }

    return this.applySingleSuitableChange(
      context,
      pendingUnsuitableMatch,
      checked,
      note
    );
  }

  async setAllSuitableValue(
    context: SuitableWorkflowContext,
    checked: boolean
  ): Promise<SetAllSuitableValueResult> {
    const action = this.getSetAllSuitableAction(
      context.matchReviews,
      checked,
      context.manufacturerMatches
    );
    if (action === "none") {
      return { action: "none" };
    }

    if (action === "confirmMarkAllAsNotSuitable") {
      return { action: "openConfirmAllUnsuitable" };
    }

    if (action === "confirmUnmarkAllSuitable") {
      return { action: "openConfirmUnmarkAllSuitable" };
    }

    await this.applyAllSuitableReviewValues(
      context.customer,
      context.composedID,
      context.manufacturerPartNumberClean,
      context.matchReviews,
      checked,
      context.manufacturerMatches
    );

    return { action: "applied", updatedManufacturer: null };
  }

  async applySuitableValue(
    customer: string,
    composedID: string,
    manufacturerPartNumberClean: string,
    matchReviews: MatchReviews,
    match: Z2DataMatch,
    checked: boolean,
    note: string = ""
  ) {
    if (!matchReviews.matches) {
      matchReviews.matches = [];
    }

    const normalizedNote = this.normalizeNote(note);
    const existingMatchReview = matchReviews.matches.find(
      (matchReview: MatchReview) =>
        Number(matchReview.partId) === this.getMatchPartId(match)
    );

    if (existingMatchReview) {
      existingMatchReview.notSuitable = checked;
      existingMatchReview.note = normalizedNote;
    } else {
      const matchReview = new MatchReview();
      matchReview.partId = this.getMatchPartId(match);
      matchReview.notSuitable = checked;
      matchReview.note = normalizedNote;
      matchReviews.matches.push(matchReview);
    }

    await this.saveMatchReviews(
      customer,
      composedID,
      manufacturerPartNumberClean,
      matchReviews
    );
  }

  async updateSuitableNote(
    context: SuitableWorkflowContext,
    match: Z2DataMatch,
    note: string
  ) {
    if (!this.isSuitableChecked(context.matchReviews, match)) {
      return;
    }

    const matchReview = this.getMatchReview(context.matchReviews, match);
    if (!matchReview) {
      return;
    }

    matchReview.note = this.normalizeNote(note);
    await this.saveMatchReviews(
      context.customer,
      context.composedID,
      context.manufacturerPartNumberClean,
      context.matchReviews
    );
  }

  async applyAllSuitableReviewValues(
    customer: string,
    composedID: string,
    manufacturerPartNumberClean: string,
    matchReviews: MatchReviews,
    checked: boolean,
    manufacturerMatches?: Z2DataMatch[],
    note: string = ""
  ) {
    if (!matchReviews.matches) {
      matchReviews.matches = [];
    }

    const normalizedNote = this.normalizeNote(note);

    const manufacturerPartIds = new Set(
      (manufacturerMatches || [])
        .map((match: Z2DataMatch) => this.getMatchPartId(match))
        .filter((partId: number) => !Number.isNaN(partId))
    );

    if (manufacturerPartIds.size > 0) {
      const existingReviewsByPartId = new Map<number, MatchReview>();
      matchReviews.matches.forEach((matchReview: MatchReview) => {
        const partId = Number(matchReview.partId);
        if (!Number.isNaN(partId)) {
          existingReviewsByPartId.set(partId, matchReview);
        }
      });

      // Add missing entries when manufacturer matches changed meanwhile.
      manufacturerPartIds.forEach((partId: number) => {
        if (existingReviewsByPartId.has(partId)) {
          return;
        }

        const matchReview = new MatchReview();
        matchReview.partId = partId;
        matchReview.notSuitable = false;
        matchReviews.matches.push(matchReview);
      });

      matchReviews.matches.forEach((matchReview: MatchReview) => {
        const partId = Number(matchReview.partId);
        if (manufacturerPartIds.has(partId)) {
          matchReview.notSuitable = checked;
          matchReview.note = normalizedNote;
          return;
        }

        // Prevent outdated reviews from forcing select-all mismatch after bulk mark.
        if (checked) {
          matchReview.notSuitable = false;
          matchReview.note = "";
        }
      });
    } else {
      matchReviews.matches.forEach((matchReview: MatchReview) => {
        matchReview.notSuitable = checked;
        matchReview.note = normalizedNote;
      });
    }

    await this.saveMatchReviews(
      customer,
      composedID,
      manufacturerPartNumberClean,
      matchReviews
    );
  }

  async confirmMarkAllAsNotSuitable(
    context: SuitableWorkflowContext,
    note: string = ""
  ): Promise<Manufacturer | null> {
    if (
      !this.hasAnyMatches(context.matchReviews) &&
      !this.hasAnyManufacturerMatches(context.manufacturerMatches)
    ) {
      return null;
    }

    await this.applyAllSuitableReviewValues(
      context.customer,
      context.composedID,
      context.manufacturerPartNumberClean,
      context.matchReviews,
      true,
      context.manufacturerMatches,
      note
    );

    const updatedManufacturer = this.cloneManufacturer(context.currentManufacturer);
    updatedManufacturer.obsolescenceStatus = "";
    updatedManufacturer.matchReview = true;
    updatedManufacturer.numberOfMatches = "";
    updatedManufacturer.matchesCount = "";
    return updatedManufacturer;
  }

  async confirmUnmarkAllSuitable(
    context: SuitableWorkflowContext,
    note: string = ""
  ): Promise<Manufacturer | null> {
    if (
      !this.hasAnyMatches(context.matchReviews) &&
      !this.hasAnyManufacturerMatches(context.manufacturerMatches)
    ) {
      return null;
    }

    await this.applyAllSuitableReviewValues(
      context.customer,
      context.composedID,
      context.manufacturerPartNumberClean,
      context.matchReviews,
      false,
      context.manufacturerMatches,
      note
    );

    const updatedManufacturer = this.cloneManufacturer(context.currentManufacturer);
    updatedManufacturer.obsolescenceStatus = "";
    updatedManufacturer.matchReview = false;
    this.restoreAvailableMatchesCount(
      updatedManufacturer,
      context.manufacturerMatches
    );
    return updatedManufacturer;
  }

  async applySingleSuitableChange(
    context: SuitableWorkflowContext,
    match: Z2DataMatch,
    checked: boolean,
    note: string = ""
  ): Promise<Manufacturer | null> {
    await this.applySuitableValue(
      context.customer,
      context.composedID,
      context.manufacturerPartNumberClean,
      context.matchReviews,
      match,
      checked,
      note
    );

    const updatedManufacturer = this.cloneManufacturer(context.currentManufacturer);
    updatedManufacturer.obsolescenceStatus = "";
    updatedManufacturer.matchReview = false;
    updatedManufacturer.edited = false;

    if (!checked) {
      this.restoreAvailableMatchesCount(
        updatedManufacturer,
        context.manufacturerMatches
      );
      return updatedManufacturer;
    }

    if (
      this.isAllSuitableChecked(
        context.matchReviews,
        context.manufacturerMatches
      )
    ) {
      updatedManufacturer.matchReview = true;
      updatedManufacturer.numberOfMatches = "";
      updatedManufacturer.matchesCount = "";
      return updatedManufacturer;
    }

    if (!this.hasAtLeastOneNotSuitable(context.matchReviews)) {
      return null;
    }

    return updatedManufacturer;
  }

  async persistManufacturerStatus(
    currentManufacturer: Manufacturer,
    updatedManufacturer: Manufacturer | null,
    updateManufacturer: UpdateManufacturerFn
  ): Promise<Manufacturer | null> {
    if (updatedManufacturer == null) {
      return null;
    }

    const persistedManufacturer = await updateManufacturer(
      updatedManufacturer,
      currentManufacturer
    );

    return persistedManufacturer || updatedManufacturer;
  }

  hasAtLeastOneNotSuitable(matchReviews: MatchReviews | undefined) {
    return !!matchReviews?.matches?.some(
      (matchReview: MatchReview) => matchReview.notSuitable === true
    );
  }

  private normalizeNote(note: string | undefined) {
    return note == null ? "" : note.trim();
  }

  private getMatchReview(
    matchReviews: MatchReviews | undefined,
    match: Z2DataMatch
  ) {
    if (!matchReviews || !Array.isArray(matchReviews.matches)) {
      return undefined;
    }

    return matchReviews.matches.find(
      (review: MatchReview) =>
        Number(review.partId) === this.getMatchPartId(match)
    );
  }

  private async saveMatchReviews(
    customer: string,
    composedID: string,
    manufacturerPartNumberClean: string,
    matchReviews: MatchReviews
  ) {
    const reviewComposedID = (composedID || "").trim();
    const reviewLookupKey = (manufacturerPartNumberClean || "").trim();
    if (reviewComposedID === "" || reviewLookupKey === "") {
      return;
    }

    matchReviews.composedID = reviewComposedID;
    matchReviews.manufacturerPartNumber = reviewLookupKey;
    if (matchReviews._id === "") {
      delete matchReviews._id;
    }
    if (matchReviews._rev === "") {
      delete matchReviews._rev;
    }

    const savedMatchReviews = await updateMatchReviews(customer, matchReviews);
    if (
      !savedMatchReviews?._id ||
      savedMatchReviews.composedID !== reviewComposedID ||
      savedMatchReviews.manufacturerPartNumber !== reviewLookupKey
    ) {
      throw new Error("The match review was not confirmed as saved.");
    }

    this.syncCurrentMatchReviews(matchReviews, savedMatchReviews);
  }

  private cloneManufacturer(currentManufacturer: Manufacturer): Manufacturer {
    const updatedManufacturer = new Manufacturer();
    Object.assign(updatedManufacturer, currentManufacturer);
    return updatedManufacturer;
  }

  private restoreAvailableMatchesCount(
    manufacturer: Manufacturer,
    manufacturerMatches?: Z2DataMatch[]
  ) {
    const availableMatchesCount = Array.isArray(manufacturerMatches)
      ? manufacturerMatches.length
      : 0;

    manufacturer.numberOfMatches =
      availableMatchesCount > 0 ? String(availableMatchesCount) : "";
    manufacturer.matchesCount =
      availableMatchesCount > 0 ? String(availableMatchesCount) : "";
  }

  private syncCurrentMatchReviews(
    currentMatchReviews: MatchReviews,
    savedMatchReviews: MatchReviews | undefined
  ) {
    if (!savedMatchReviews) {
      return;
    }

    if (savedMatchReviews._id != null) {
      currentMatchReviews._id = savedMatchReviews._id;
    }
    if (savedMatchReviews._rev != null) {
      currentMatchReviews._rev = savedMatchReviews._rev;
    }
    if (savedMatchReviews.manufacturerPartNumber != null) {
      currentMatchReviews.manufacturerPartNumber =
        savedMatchReviews.manufacturerPartNumber;
    }
    if (savedMatchReviews.composedID != null) {
      currentMatchReviews.composedID = savedMatchReviews.composedID;
    }
    if (savedMatchReviews.type != null) {
      currentMatchReviews.type = savedMatchReviews.type;
    }
    if (Array.isArray(savedMatchReviews.matches)) {
      currentMatchReviews.matches = savedMatchReviews.matches;
    }
  }
}
